<template>
  <div class="energy-management-platform">
    <!-- 背景装饰 -->
    <div class="background-decoration">
      <div class="grid-overlay"></div>
      <div class="blue-glow"></div>
    </div>

    <!-- 顶部标题栏 -->
    <header class="top-header">
      <div class="header-left">
        <div class="logo">
          <div class="logo-icon">⚡</div>
          <h2>能碳管理监控平台</h2>
        </div>
        <nav class="main-nav">
          <ul class="nav-list">
            <li class="nav-item">
              <a href="#" class="nav-link active">实时监控</a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" @click.stop="toggleDeviceList">设备列表</a>
            </li>
            <li class="nav-item">
              <a href="#/carbon-emission" class="nav-link">碳排放</a>
            </li>
            <li class="nav-item">
              <a href="#/carbon-emission" class="nav-link">空压机节能</a>
            </li>
            <li class="nav-item">
              <a href="#/carbon-emission" class="nav-link">冷热源节能</a>
            </li>
          </ul>
        </nav>
      </div>
      <div class="header-right">
        <div class="system-status">
          <div class="status-item">
            <div class="status-dot online"></div>
            <span>系统运行正常</span>
          </div>
          <div class="status-item">
            <span class="status-value">{{ currentTime }}</span>
          </div>
        </div>
        <el-dropdown trigger="click" @command="handleCommand">
          <div class="user-info">
            <span>{{ username }}</span>
            <div class="user-avatar">👤</div>
          </div>
          <el-dropdown-menu slot="dropdown" class="dark-dropdown-menu">
            <el-dropdown-item command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </header>

    <!-- 设备列表弹窗 -->
    <div class="device-list-modal" v-if="showDeviceList">
      <div class="modal-overlay" @click="closeDeviceList"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h2>设备列表</h2>
          <div class="close-btn" @click="closeDeviceList">×</div>
        </div>
        <div class="modal-body">
          <div class="device-table-container">
            <table class="device-table">
              <thead>
                <tr>
                  <th>设备名称</th>
                  <th>设备类型</th>
                  <th>所属区域</th>
                  <th>设备状态</th>
                  <th>功率</th>
                  <th>运行时长</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in devices" :key="device.id" :class="device.status">
                  <td>{{ device.name }}</td>
                  <td>{{ device.type }}</td>
                  <td>{{ device.area }}</td>
                  <td>
                    <span class="status-badge" :class="device.status">
                      {{ device.statusText }}
                    </span>
                  </td>
                  <td>{{ device.power }}kW</td>
                  <td>{{ device.runTime }}</td>
                  <td>
                    <button class="action-btn" @click.stop="showDeviceDetail(device)">查看详情</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 设备详情弹窗 -->
    <div class="device-detail-modal" v-if="showDeviceDetailModal">
      <div class="modal-overlay" @click="closeDeviceDetail"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h2>设备详情</h2>
          <div class="close-btn" @click="closeDeviceDetail">×</div>
        </div>
        <div class="modal-body">
          <div class="device-detail-content">
            <!-- 设备基本信息 -->
            <div class="device-basic-info" v-if="selectedDevice">
              <h3 class="section-title">基本信息</h3>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">设备名称</span>
                  <span class="info-value highlight">{{ selectedDevice.name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">设备类型</span>
                  <span class="info-value">{{ selectedDevice.type }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">所属区域</span>
                  <span class="info-value">{{ selectedDevice.area }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">设备状态</span>
                  <span class="info-value">
                    <span class="status-badge" :class="selectedDevice.status">
                      {{ selectedDevice.statusText }}
                    </span>
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">功率</span>
                  <span class="info-value">{{ selectedDevice.power }}kW</span>
                </div>
                <div class="info-item">
                  <span class="info-label">运行时长</span>
                  <span class="info-value">{{ selectedDevice.runTime }}</span>
                </div>
              </div>
            </div>

            <!-- 设备控制区域 -->
            <div class="device-control-section">
              <h3 class="section-title">设备控制</h3>

              <!-- 运行频率控制 -->
              <div class="control-section">
                <h4 class="control-subtitle">运行频率</h4>
                <div class="frequency-control">
                  <div class="frequency-input-group">
                    <div class="frequency-input-container">
                      <input type="number" class="frequency-input" min="0" max="100" v-model.number="frequency">
                      <span class="frequency-unit">HZ</span>
                      <button class="frequency-btn" @click="updateFrequency">更新频率</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 设备开关控制 -->
              <div class="control-section">
                <h4 class="control-subtitle">设备开关</h4>
                <div class="control-buttons">
                  <button class="control-btn start-btn" @click="startDevice">开启设备</button>
                  <button class="control-btn stop-btn" @click="stopDevice">关闭设备</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 告警详情弹窗 -->
    <div class="alarm-detail-modal" v-if="showAlarmDetailModal">
      <div class="modal-overlay" @click="closeAlarmDetail"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h2>告警详情</h2>
          <div class="close-btn" @click="closeAlarmDetail">×</div>
        </div>
        <div class="modal-body">
          <div class="alarm-detail-content">
            <div class="alarm-table-container">
              <table class="alarm-table">
                <thead>
                  <tr>
                    <th>设备名称</th>
                    <th>所属区域</th>
                    <th>告警原因</th>
                    <th>告警时间</th>
                    <th>处理状态</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="alarm in alarmDevices" :key="alarm.id">
                    <td>{{ alarm.deviceName }}</td>
                    <td>{{ alarm.area }}</td>
                    <td>{{ alarm.reason }}</td>
                    <td>{{ alarm.time }}</td>
                    <td>
                      <span class="status-badge" :class="alarm.status">{{ alarm.statusText }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <main class="main-content">
      <!-- 关键指标统计 -->
      <section class="metrics-section">
        <div class="metric-card">
          <div class="metric-header">
            <h3>原始用电量</h3>
            <div class="metric-icon">💡</div>
          </div>
          <div class="metric-value">{{ Math.round(metrics.originalPower.display).toLocaleString() }}<span class="unit">KW·h</span></div>
          <div class="metric-trend up">
            <span class="trend-icon">↑</span>
            <span>12.5%</span>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-header">
            <h3>节能后耗电</h3>
            <div class="metric-icon">💡</div>
          </div>
          <div class="metric-value">{{ Math.round(metrics.savedPower.display).toLocaleString() }}<span class="unit">KW·h</span></div>
          <div class="metric-trend up">
            <span class="trend-icon">↑</span>
            <span>8.3%</span>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-header">
            <h3>共节省电量</h3>
            <div class="metric-icon">💡</div>
          </div>
          <div class="metric-value">{{ Math.round(metrics.totalSaved.display).toLocaleString() }}<span class="unit">KW·h</span></div>
          <div class="metric-trend down">
            <span class="trend-icon">↓</span>
            <span>5.7%</span>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-header">
            <h3>系统改造设备</h3>
            <div class="metric-icon">⚡</div>
          </div>
          <div class="metric-value">66<span class="unit"></span></div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{width: '87%'}"></div>
          </div>
        </div>
      </section>

      <!-- 核心数据展示区 -->
      <section class="core-section">
        <!-- 左侧图表区域 -->
        <div class="left-charts">
          <!-- 传播数据柱状图 -->
          <div class="chart-card">
            <div class="chart-header">
              <h3>设备数量分布</h3>
              <div class="chart-time">2024-06-18 14:30</div>
            </div>
            <div class="chart-container">
              <canvas ref="barChart" width="350" height="200"></canvas>
            </div>
          </div>
        </div>

        <!-- 右侧数据区域 -->
        <div class="right-data">
          <!-- 设备预警功能模块 -->
          <div class="chart-card">
            <div class="chart-header">
              <h3>设备预警功能</h3>
            </div>
            <div class="chart-container">
              <div class="device-alarm-container">
                <!-- 告警图表，占满整个容器 -->
                <div class="alarm-chart-section">
                  <canvas ref="alarmChart" width="300" height="300"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- 底部三段式布局 -->
      <section class="bottom-section">
        <!-- 每台设备当日耗电量 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>设备当日耗电量</h3>
          </div>
          <div class="chart-container">
            <div ref="devicePowerChart" style="width: 100%; height: 320px;"></div>
          </div>
        </div>

        <!-- 每台设备实时功率 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>设备实时功率</h3>
          </div>
          <div class="chart-container">
            <div ref="deviceRealTimeChart" style="width: 100%; height: 320px;"></div>
          </div>
        </div>

        <!-- 总耗电量趋势对比 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>总耗电量趋势对比</h3>
            <div class="chart-tabs">
              <span class="tab-item active">今日</span>
              <span class="tab-item">本周</span>
              <span class="tab-item">本月</span>
            </div>
          </div>
          <div class="chart-container">
            <div ref="trendChart" style="width: 100%; height: 320px;"></div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import * as echarts from 'echarts';
export default {
  name: 'EnergyManagementPlatform',
  data() {
    return {
      currentTime: '',
      username: '用户',
      chartIntervals: [],
      showDeviceList: false,
      showDeviceDetailModal: false,
      selectedDevice: null,
      frequency: 50,

      // 关键指标数据
      metrics: {
        originalPower: { display: 2509, target: 2509 },
        savedPower: { display: 1017, target: 1017 },
        totalSaved: { display: 12458, target: 12458 }
      },
      // Plexus神经网络网格相关数据
      mouseX: 0,
      mouseY: 0,
      hoveredCard: null,
      plexusData: {
        nodes: [],
        connections: []
      },
      animationId: null,
      devices: [
        { id: 1, name: '冷水机组A', type: '制冷设备', area: '1#机房', status: 'online', statusText: '运行中', power: '450', runTime: '128h' },
        { id: 2, name: '冷水机组B', type: '制冷设备', area: '1#机房', status: 'online', statusText: '运行中', power: '380', runTime: '96h' },
        { id: 3, name: '冷却水泵1', type: '水泵', area: '2#机房', status: 'warning', statusText: '告警', power: '120', runTime: '240h' },
        { id: 4, name: '冷却水泵2', type: '水泵', area: '2#机房', status: 'online', statusText: '运行中', power: '120', runTime: '168h' },
        { id: 5, name: '新风机1', type: '通风设备', area: '屋顶', status: 'offline', statusText: '离线', power: '0', runTime: '0h' },
        { id: 6, name: '新风机2', type: '通风设备', area: '屋顶', status: 'online', statusText: '运行中', power: '180', runTime: '144h' },
        { id: 7, name: '照明系统', type: '电气设备', area: '全区域', status: 'online', statusText: '运行中', power: '90', runTime: '48h' },
        { id: 8, name: '电梯系统', type: '电气设备', area: '全区域', status: 'online', statusText: '运行中', power: '150', runTime: '84h' },
        { id: 9, name: '空调机组', type: '空调设备', area: '办公区', status: 'online', statusText: '运行中', power: '220', runTime: '72h' },
        { id: 10, name: '空压机', type: '动力设备', area: '3#车间', status: 'warning', statusText: '告警', power: '320', runTime: '192h' }
      ],
      // 设备预警数据
      alarmData: {
        total: 20,
        devices: 19,
        pending: 20
      },
      // 当前显示的告警数据类型索引 (0: 告警总数, 1: 告警设备, 2: 待处理)
      currentAlarmIndex: 0,
      // 告警数据类型与颜色对应关系
      alarmTypes: [
        { name: '告警总数', color: '#28a745' }, // 绿色
        { name: '待处理', color: '#ffc107' }, // 黄色
        { name: '告警设备', color: '#dc3545' }    // 红色
      ],
      // 告警设备数据
      alarmDevices: [
        { id: 1, deviceId: 3, deviceName: '冷却水泵1', area: '2#机房', reason: '温度过高', time: '2024-06-18 14:30:25', status: 'warning', statusText: '未处理' },
        { id: 2, deviceId: 10, deviceName: '空压机', area: '3#车间', reason: '压力异常', time: '2024-06-18 14:25:10', status: 'warning', statusText: '未处理' }
      ],
      // 告警详情弹窗
      showAlarmDetailModal: false,
      selectedAlarm: null,
      // 鼠标悬停状态
      hoveredAlarmId: null
    };
  },
  mounted() {
    this.updateTime();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.user_name) {
      this.username = user.user_name;
    }
    setInterval(this.updateTime, 1000);
    this.initCharts();
    this.initPlexus();
    // 调用API获取数据
    this.fetchEnergyData();
    // 设置指标数据刷新定时器，每隔30秒刷新一次
    const metricsInterval = setInterval(() => {
      this.refreshMetrics();
    }, 30000);
    this.chartIntervals.push(metricsInterval);
  },
  beforeDestroy() {
    this.chartIntervals.forEach(interval => clearInterval(interval));
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    // 清理设备预警画布的鼠标事件监听器
    const alarmCanvas = this.$refs.alarmChart;
    if (alarmCanvas) {
      alarmCanvas.removeEventListener('mousemove', this.handleAlarmMouseMove);
      alarmCanvas.removeEventListener('click', this.handleAlarmClick);
    }
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    // 数字滚动动画
    animateNumber(key, target, duration = 1000) {
      const obj = this.metrics[key];
      const start = obj.display;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // 使用缓动函数使动画更自然
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        const easeProgress = easeOutCubic(progress);

        obj.display = start + (target - start) * easeProgress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          obj.display = target;
        }
      };

      requestAnimationFrame(animate);
    },

    // 刷新指标数据
    refreshMetrics() {
      // 调用API获取最新数据
      this.fetchEnergyData();
    },
    // 处理鼠标移动事件
    handleMouseMove(e) {
      const rect = this.$el.querySelector('.core-panel-area').getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    },
    // 初始化Plexus神经网络网格
    initPlexus() {
      const canvas = this.$refs.plexusCanvas;
      if (!canvas) return;

      // 设置canvas尺寸
      const container = canvas.parentElement;
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;

      // 创建节点
      const nodeCount = 50;
      const nodes = [];

      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          color: Math.random() > 0.5 ? '#00d4ff' : '#ff00ff',
          pulse: Math.random()
        });
      }

      this.plexusData.nodes = nodes;
      this.animatePlexus();
    },
    // 绘制Plexus神经网络网格
    drawPlexus() {
      const canvas = this.$refs.plexusCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const nodes = this.plexusData.nodes;
      const mouseX = this.mouseX;
      const mouseY = this.mouseY;

      // 清空画布
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 绘制连接线
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            // 计算线条透明度
            const opacity = (100 - distance) / 100 * 0.3;

            // 设置线条颜色，交替使用青色和洋红色
            if (i % 2 === 0) {
              ctx.strokeStyle = `rgba(0, 212, 255, ${opacity})`;
            } else {
              ctx.strokeStyle = `rgba(255, 0, 255, ${opacity})`;
            }

            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();

            // 绘制流光效果
            if (distance < 80) {
              const midX = (nodes[i].x + nodes[j].x) / 2;
              const midY = (nodes[i].y + nodes[j].y) / 2;
              const glowOpacity = opacity * 2;

              ctx.fillStyle = `rgba(0, 212, 255, ${glowOpacity})`;
              ctx.beginPath();
              ctx.arc(midX, midY, 1, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      }

      // 绘制节点
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        // 计算节点与鼠标的距离
        const dx = node.x - mouseX;
        const dy = node.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // 节点向鼠标方向移动
        if (distance < 150) {
          const force = (150 - distance) / 150 * 2;
          node.x += (mouseX - node.x) * force * 0.01;
          node.y += (mouseY - node.y) * force * 0.01;
        }

        // 绘制节点光晕
        const glowSize = node.size * 5 + Math.sin(node.pulse) * 2;
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowSize);
        gradient.addColorStop(0, `${node.color}66`);
        gradient.addColorStop(1, `${node.color}00`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowSize, 0, Math.PI * 2);
        ctx.fill();

        // 绘制节点核心
        ctx.fillStyle = node.color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fill();

        // 更新节点脉冲
        node.pulse += 0.05;
      }
    },
    // 更新Plexus神经网络网格
    updatePlexus() {
      const nodes = this.plexusData.nodes;
      const canvas = this.$refs.plexusCanvas;
      if (!canvas) return;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        // 更新节点位置
        node.x += node.vx;
        node.y += node.vy;

        // 边界检测
        if (node.x < 0 || node.x > canvas.width) {
          node.vx *= -1;
          node.x = Math.max(0, Math.min(canvas.width, node.x));
        }
        if (node.y < 0 || node.y > canvas.height) {
          node.vy *= -1;
          node.y = Math.max(0, Math.min(canvas.height, node.y));
        }
      }
    },
    // 动画循环
    animatePlexus() {
      this.updatePlexus();
      this.drawPlexus();
      this.animationId = requestAnimationFrame(() => this.animatePlexus());
    },
    toggleDeviceList() {
      this.showDeviceList = !this.showDeviceList;
    },
    closeDeviceList() {
      this.showDeviceList = false;
    },
    showDeviceDetail(device) {
      this.selectedDevice = device;
      this.showDeviceDetailModal = true;
      // 初始化频率值
      this.frequency = 50;
    },
    closeDeviceDetail() {
      this.showDeviceDetailModal = false;
      this.selectedDevice = null;
    },
    startDevice() {
      if (this.selectedDevice) {
        this.selectedDevice.status = 'online';
        this.selectedDevice.statusText = '运行中';
        alert(`已开启设备: ${this.selectedDevice.name}`);
      }
    },
    stopDevice() {
      if (this.selectedDevice) {
        this.selectedDevice.status = 'offline';
        this.selectedDevice.statusText = '离线';
        alert(`已关闭设备: ${this.selectedDevice.name}`);
      }
    },
    updateFrequency() {
      if (this.selectedDevice) {
        alert(`已更新设备 ${this.selectedDevice.name} 的运行频率为 ${this.frequency}%`);
      }
    },
    // 关闭告警详情
    closeAlarmDetail() {
      this.showAlarmDetailModal = false;
    },
    initCharts() {
      // 使用setTimeout延迟绘制，避免页面初始化时的性能高峰
      setTimeout(() => {
        // 使用requestAnimationFrame优化图表绘制
        requestAnimationFrame(() => {
          this.drawBarChart();
          this.drawAlarmChart();
          this.initECharts();
        });

        // 添加设备预警画布的鼠标事件监听器
        const alarmCanvas = this.$refs.alarmChart;
        if (alarmCanvas) {
          alarmCanvas.addEventListener('mousemove', this.handleAlarmMouseMove);
          alarmCanvas.addEventListener('click', this.handleAlarmClick);
        }
      }, 500);
    },
    // 初始化ECharts图表
    initECharts() {
      this.initDevicePowerChart();
      this.initDeviceRealTimeChart();
      this.initTotalPowerChart();
    },
    // 初始化每台设备当日耗电量图表
    initDevicePowerChart() {
      const chartDom = this.$refs.devicePowerChart;
      if (!chartDom) return;

      const myChart = echarts.init(chartDom);

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['冷水机组A', '冷水机组B', '冷却水泵1', '冷却水泵2', '新风机2'],
          textStyle: {
            color: '#ffffff'
          },
          top: '0%'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '22%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '耗电量 (KW·h)',
          nameTextStyle: {
            color: '#b0c4de'
          },
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.1)'
            }
          }
        },
        series: [
          {
            name: '冷水机组A',
            type: 'line',
            data: [120, 132, 101, 134, 90, 230, 210],
            smooth: true,
            lineStyle: {
              color: '#00d4ff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(0, 212, 255, 0.3)' },
                { offset: 1, color: 'rgba(0, 212, 255, 0.1)' }
              ])
            }
          },
          {
            name: '冷水机组B',
            type: 'line',
            data: [220, 182, 191, 234, 290, 330, 310],
            smooth: true,
            lineStyle: {
              color: '#28a745'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(40, 167, 69, 0.3)' },
                { offset: 1, color: 'rgba(40, 167, 69, 0.1)' }
              ])
            }
          },
          {
            name: '冷却水泵1',
            type: 'line',
            data: [150, 232, 201, 154, 190, 330, 410],
            smooth: true,
            lineStyle: {
              color: '#ffc107'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255, 193, 7, 0.3)' },
                { offset: 1, color: 'rgba(255, 193, 7, 0.1)' }
              ])
            }
          },
          {
            name: '冷却水泵2',
            type: 'line',
            data: [320, 332, 301, 334, 390, 330, 320],
            smooth: true,
            lineStyle: {
              color: '#dc3545'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(220, 53, 69, 0.3)' },
                { offset: 1, color: 'rgba(220, 53, 69, 0.1)' }
              ])
            }
          },
          {
            name: '新风机2',
            type: 'line',
            data: [820, 932, 901, 934, 1290, 1330, 1320],
            smooth: true,
            lineStyle: {
              color: '#6f42c1'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(111, 66, 193, 0.3)' },
                { offset: 1, color: 'rgba(111, 66, 193, 0.1)' }
              ])
            }
          }
        ]
      };

      myChart.setOption(option);

      // 监听窗口大小变化，调整图表大小
      window.addEventListener('resize', () => {
        myChart.resize();
      });
    },
    // 初始化每台设备实时功率图表
    initDeviceRealTimeChart() {
      const chartDom = this.$refs.deviceRealTimeChart;
      if (!chartDom) return;

      const myChart = echarts.init(chartDom);

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['冷水机组A', '冷水机组B', '冷却水泵1', '冷却水泵2', '新风机2'],
          textStyle: {
            color: '#ffffff'
          },
          top: '0%'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '22%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '功率 (kW)',
          nameTextStyle: {
            color: '#b0c4de'
          },
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.1)'
            }
          }
        },
        series: [
          {
            name: '冷水机组A',
            type: 'line',
            data: [450, 460, 455, 465, 450, 470, 460],
            smooth: true,
            lineStyle: {
              color: '#00d4ff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(0, 212, 255, 0.3)' },
                { offset: 1, color: 'rgba(0, 212, 255, 0.1)' }
              ])
            }
          },
          {
            name: '冷水机组B',
            type: 'line',
            data: [380, 385, 382, 388, 380, 390, 385],
            smooth: true,
            lineStyle: {
              color: '#28a745'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(40, 167, 69, 0.3)' },
                { offset: 1, color: 'rgba(40, 167, 69, 0.1)' }
              ])
            }
          },
          {
            name: '冷却水泵1',
            type: 'line',
            data: [120, 125, 122, 128, 120, 130, 125],
            smooth: true,
            lineStyle: {
              color: '#ffc107'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255, 193, 7, 0.3)' },
                { offset: 1, color: 'rgba(255, 193, 7, 0.1)' }
              ])
            }
          },
          {
            name: '冷却水泵2',
            type: 'line',
            data: [120, 122, 121, 123, 120, 125, 122],
            smooth: true,
            lineStyle: {
              color: '#dc3545'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(220, 53, 69, 0.3)' },
                { offset: 1, color: 'rgba(220, 53, 69, 0.1)' }
              ])
            }
          },
          {
            name: '新风机2',
            type: 'line',
            data: [180, 185, 182, 188, 180, 190, 185],
            smooth: true,
            lineStyle: {
              color: '#6f42c1'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(111, 66, 193, 0.3)' },
                { offset: 1, color: 'rgba(111, 66, 193, 0.1)' }
              ])
            }
          }
        ]
      };

      myChart.setOption(option);

      // 监听窗口大小变化，调整图表大小
      window.addEventListener('resize', () => {
        myChart.resize();
      });
    },
    // 初始化总耗电量趋势对比图表
    initTotalPowerChart() {
      const chartDom = this.$refs.trendChart;
      if (!chartDom) return;

      const myChart = echarts.init(chartDom);

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['当前耗电量', '原始耗电量'],
          textStyle: {
            color: '#ffffff'
          },
          top: '0%'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '22%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '耗电量 (KW·h)',
          nameTextStyle: {
            color: '#b0c4de'
          },
          axisLabel: {
            color: '#b0c4de'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.3)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 212, 255, 0.1)'
            }
          }
        },
        series: [
          {
            name: '当前耗电量',
            type: 'line',
            data: [1200, 1320, 1010, 1340, 900, 2300, 2100],
            smooth: true,
            lineStyle: {
              color: '#00d4ff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(0, 212, 255, 0.3)' },
                { offset: 1, color: 'rgba(0, 212, 255, 0.1)' }
              ])
            }
          },
          {
            name: '原始耗电量',
            type: 'line',
            data: [1350, 1470, 1160, 1490, 1050, 2450, 2250],
            smooth: true,
            lineStyle: {
              color: '#28a745'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(40, 167, 69, 0.3)' },
                { offset: 1, color: 'rgba(40, 167, 69, 0.1)' }
              ])
            }
          }
        ]
      };

      myChart.setOption(option);

      // 监听窗口大小变化，调整图表大小
      window.addEventListener('resize', () => {
        myChart.resize();
      });
    },
    // 处理设备预警画布的鼠标移动事件
    handleAlarmMouseMove(e) {
      const canvas = this.$refs.alarmChart;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 计算鼠标是否在"告警设备"标签上面
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // "告警设备"标签区域（与绘制位置一致）
      const alarmDeviceLabelArea = {
        x: centerX - 60, // 标签起始X坐标
        y: centerY + 30, // 标签起始Y坐标（与绘制位置一致）
        width: 60,      // 标签宽度
        height: 30      // 标签高度
      };

      // 检查鼠标是否在"告警设备"标签区域内
      if (mouseX >= alarmDeviceLabelArea.x && mouseX <= alarmDeviceLabelArea.x + alarmDeviceLabelArea.width &&
          mouseY >= alarmDeviceLabelArea.y && mouseY <= alarmDeviceLabelArea.y + alarmDeviceLabelArea.height) {
        canvas.style.cursor = 'pointer';
        this.hoveredAlarmId = 3; // 告警设备的ID
      } else {
        canvas.style.cursor = 'default';
        this.hoveredAlarmId = null;
      }
    },
    // 处理设备预警画布的点击事件
    handleAlarmClick(e) {
      const canvas = this.$refs.alarmChart;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 计算鼠标是否在"告警设备"标签上面
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // "告警设备"标签区域
      const alarmDeviceLabelArea = {
        x: centerX - 60, // 标签起始X坐标
        y: centerY + 30, // 标签起始Y坐标（与鼠标移动事件中的坐标一致）
        width: 60,      // 标签宽度
        height: 30      // 标签高度
      };

      // 检查鼠标是否在"告警设备"标签区域内
      if (mouseX >= alarmDeviceLabelArea.x && mouseX <= alarmDeviceLabelArea.x + alarmDeviceLabelArea.width &&
          mouseY >= alarmDeviceLabelArea.y && mouseY <= alarmDeviceLabelArea.y + alarmDeviceLabelArea.height) {
        // 显示告警详情弹窗，直接显示所有告警设备
        this.showAlarmDetail();
      }
    },
    // 显示告警详情
    showAlarmDetail() {
      this.showAlarmDetailModal = true;
    },
    // 从API获取能源数据
    fetchEnergyData() {
      const apiUrl = '/otherApi/manager/getonecompanysensor/';

      fetch(apiUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          // 检查数据结构
          if (data && typeof data === 'object') {
            // 更新指标数据并触发动画
            if (data.pre_mod_power_con !== undefined) {
              this.metrics.originalPower.target = data.pre_mod_power_con;
              this.animateNumber('originalPower', data.pre_mod_power_con);
            }
            if (data.post_mod_power_con !== undefined) {
              this.metrics.savedPower.target = data.post_mod_power_con;
              this.animateNumber('savedPower', data.post_mod_power_con);
            }
            if (data.cumulative_power_savings !== undefined) {
              this.metrics.totalSaved.target = data.cumulative_power_savings;
              this.animateNumber('totalSaved', data.cumulative_power_savings);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching energy data:', error);
        });
    },
    drawBarChart() {
      const canvas = this.$refs.barChart;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const data = [20, 6, 5, 3, 8, 12, 4];
      const labels = ['空压机', '冷水机', '水泵', '风机', '照明', '空调', '其他'];
      const colors = ['#00d4ff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#17a2b8'];

      this.drawBar(ctx, data, labels, colors, canvas.width, canvas.height);
    },
    drawTop10Chart() {
      const canvas = this.$refs.top10Chart;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const data = [85, 78, 65, 58, 45, 38, 32, 25, 18];
      const labels = ['设备1', '设备2', '设备3', '设备4', '设备5', '设备6', '设备7', '设备8', '设备9'];

      this.drawHorizontalBar(ctx, data, labels, '#00d4ff', canvas.width, canvas.height);
    },

    drawAlarmChart() {
      const canvas = this.$refs.alarmChart;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // 清空画布
      ctx.clearRect(0, 0, width, height);

      // 设置中心点和半径
      const centerX = width / 2;
      const centerY = height / 2;
      const outerRadius = 100; // 增大半径，让仪表盘占更大空间
      const innerRadius = 60;

      // 获取当前时间，用于动态效果
      const time = Date.now() / 1000;

      // 1. 绘制动态背景光晕
      ctx.save();
      const glowRadius = outerRadius + 20 + Math.sin(time * 2) * 5;
      ctx.shadowColor = '#ff6b35';
      ctx.shadowBlur = 30 + Math.sin(time * 1.5) * 10;
      ctx.beginPath();
      ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 107, 53, ${0.1 + Math.sin(time * 2) * 0.05})`;
      ctx.fill();
      ctx.restore();

      // 2. 绘制动态粒子效果
      ctx.save();
      for (let i = 0; i < 15; i++) {
        const angle = (i / 15) * Math.PI * 2 + time;
        const radius = outerRadius + 10 + Math.sin(time * 3 + i) * 15;
        const particleX = centerX + Math.cos(angle) * radius;
        const particleY = centerY + Math.sin(angle) * radius;
        const particleSize = 1 + Math.sin(time * 4 + i) * 0.5;

        ctx.fillStyle = `rgba(255, 107, 53, ${0.3 + Math.sin(time * 2 + i) * 0.2})`;
        ctx.shadowColor = '#ff6b35';
        ctx.shadowBlur = 5;
        ctx.beginPath();
        ctx.arc(particleX, particleY, particleSize, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // 3. 绘制外边框
      ctx.save();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // 4. 绘制刻度线
      ctx.save();
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 1;

      // 主刻度线（12条）
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const startX = centerX + Math.cos(angle) * (outerRadius - 8);
        const startY = centerY + Math.sin(angle) * (outerRadius - 8);
        const endX = centerX + Math.cos(angle) * outerRadius;
        const endY = centerY + Math.sin(angle) * outerRadius;

        // 动态刻度效果
        const glowIntensity = Math.sin(time * 3 + i) * 0.5 + 0.5;
        ctx.strokeStyle = `rgba(255, 107, 53, ${0.3 + glowIntensity * 0.3})`;
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }

      // 副刻度线（36条）
      ctx.lineWidth = 0.5;
      for (let i = 0; i < 36; i++) {
        const angle = (i / 36) * Math.PI * 2 - Math.PI / 2;
        const startX = centerX + Math.cos(angle) * (outerRadius - 4);
        const startY = centerY + Math.sin(angle) * (outerRadius - 4);
        const endX = centerX + Math.cos(angle) * outerRadius;
        const endY = centerY + Math.sin(angle) * outerRadius;

        ctx.strokeStyle = '#555';
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }
      ctx.restore();

      // 5. 绘制发光进度条（橙色部分）
      ctx.save();
      const progressAngle = (220 / 360) * Math.PI * 2; // 约220度的进度条
      const startProgress = -Math.PI / 2;
      const endProgress = startProgress + progressAngle;

      // 发光效果
      ctx.shadowColor = '#ff6b35';
      ctx.shadowBlur = 20 + Math.sin(time * 2) * 5;

      // 绘制进度条背景
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius - 12, startProgress, endProgress);
      ctx.lineWidth = 10;
      ctx.strokeStyle = '#ff6b35';
      ctx.stroke();

      // 绘制进度条分段效果
      ctx.lineWidth = 8;
      for (let i = 0; i < 11; i++) {
        const segmentStart = startProgress + (i / 10) * progressAngle;
        const segmentEnd = segmentStart + (0.8 / 10) * progressAngle;
        // 动态分段亮度
        const brightness = 0.7 + Math.sin(time * 2 + i) * 0.3;
        ctx.strokeStyle = `rgba(255, 154, 106, ${brightness})`;
        ctx.beginPath();
        ctx.arc(centerX, centerY, outerRadius - 12, segmentStart, segmentEnd);
        ctx.stroke();
      }
      ctx.restore();

      // 6. 绘制旋转指针
      ctx.save();
      const pointerAngle = -Math.PI / 2 + (time * 0.5 % (Math.PI * 2));
      const pointerLength = outerRadius - 15;
      ctx.translate(centerX, centerY);
      ctx.rotate(pointerAngle);

      // 指针发光效果
      ctx.shadowColor = '#ff6b35';
      ctx.shadowBlur = 15;

      // 绘制指针
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(pointerLength, 0);
      ctx.stroke();

      // 绘制指针尖端
      ctx.fillStyle = '#ff6b35';
      ctx.beginPath();
      ctx.moveTo(pointerLength, -3);
      ctx.lineTo(pointerLength + 5, 0);
      ctx.lineTo(pointerLength, 3);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // 7. 绘制内部数据面板背景
      ctx.save();
      ctx.fillStyle = 'rgba(10, 20, 40, 0.8)';
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // 8. 绘制闪烁警告标志
      ctx.save();
      const warningAlpha = 0.7 + Math.sin(time * 4) * 0.3;
      ctx.fillStyle = `rgba(255, 107, 53, ${warningAlpha})`;
      ctx.shadowColor = '#ff6b35';
      ctx.shadowBlur = 15 + Math.sin(time * 4) * 5;

      // 绘制三角形警告标志
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - 15);
      ctx.lineTo(centerX - 10, centerY + 5);
      ctx.lineTo(centerX + 10, centerY + 5);
      ctx.closePath();
      ctx.fill();

      // 绘制感叹号
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', centerX, centerY - 2);
      ctx.restore();

      // 9. 绘制数据列表
      ctx.save();
      const dataItems = [
        { label: '告警总数', value: '91', color: '#ff6b35' },
        { label: '待处理', value: '19', color: '#ff6b35' },
        { label: '告警设备', value: '29', color: '#ff6b35' }
      ];

      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      dataItems.forEach((item, index) => {
        const yPos = centerY - 25 + index * 25;

        // 绘制标签
        ctx.fillStyle = '#888';
        ctx.font = '14px Arial';
        ctx.fillText(item.label, centerX - 50, yPos);

        // 动态数值发光效果
        const valueGlow = 10 + Math.sin(time * 3 + index) * 5;
        ctx.fillStyle = item.color;
        ctx.shadowColor = item.color;
        ctx.shadowBlur = valueGlow;
        ctx.font = 'bold 16px Arial';
        ctx.fillText(item.value, centerX + 20, yPos);
      });
      ctx.restore();

      // 10. 绘制右侧警告面板
      ctx.save();
      ctx.translate(centerX + 70, centerY - 130);

      // 警告面板背景（动态闪烁）
      const warningBgAlpha = 0.1 + Math.sin(time * 4) * 0.05;
      ctx.fillStyle = `rgba(255, 107, 53, ${warningBgAlpha})`;
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 1;
      ctx.beginPath();
      // 优化为圆角矩形
      const rectW = 80;
      const rectH = 30;
      const radius = 6;
      if (ctx.roundRect) {
        ctx.roundRect(0, 0, rectW, rectH, radius);
      } else {
        // 兼容性处理
        ctx.moveTo(radius, 0);
        ctx.lineTo(rectW - radius, 0);
        ctx.quadraticCurveTo(rectW, 0, rectW, radius);
        ctx.lineTo(rectW, rectH - radius);
        ctx.quadraticCurveTo(rectW, rectH, rectW - radius, rectH);
        ctx.lineTo(radius, rectH);
        ctx.quadraticCurveTo(0, rectH, 0, rectH - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
      }
      ctx.fill();
      ctx.stroke();

      // 警告标志
      ctx.fillStyle = '#ff6b35';
      ctx.font = '13px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚠', 5, 15);

      // 警告文字
      ctx.fillStyle = '#ff6b35';
      ctx.font = '11px Arial';
      ctx.fillText('设备异常', 25, 15);

      // 绘制动态警告波形
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, 25);
      for (let x = 20; x <= 80; x += 10) {
        const y = 25 + Math.sin((x / 10) + time * 6) * 5;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();

      // 12. 请求下一帧动画
      requestAnimationFrame(() => this.drawAlarmChart());
    },
    drawBar(ctx, data, labels, colors, width, height) {
      const padding = 40;
      const chartWidth = width - padding * 2;
      const chartHeight = height - padding * 2;
      const barWidth = chartWidth / data.length * 0.6;
      const gap = chartWidth / data.length * 0.4;
      const maxValue = Math.max(...data);

      ctx.clearRect(0, 0, width, height);

      // 绘制单位标识
      ctx.fillStyle = '#00d4ff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('单位：KW·h', width - padding - 180, padding - 60);

      // 绘制网格
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.1)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();

        // 绘制Y轴刻度值
        const value = maxValue - (maxValue / 5) * i;
        ctx.fillStyle = '#b0c4de';
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(Math.round(value), padding - 10, y + 4);
      }

      // 绘制Y轴
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padding, padding);
      ctx.lineTo(padding, height - padding);
      ctx.stroke();

      // 绘制X轴
      ctx.beginPath();
      ctx.moveTo(padding, height - padding);
      ctx.lineTo(width - padding, height - padding);
      ctx.stroke();

      // 绘制柱状图
      data.forEach((value, index) => {
        const x = padding + index * (barWidth + gap);
        const barHeight = (value / maxValue) * chartHeight;
        const y = padding + chartHeight - barHeight;

        ctx.fillStyle = colors[index % colors.length];
        ctx.fillRect(x, y, barWidth, barHeight);

        // 绘制数值
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(value, x + barWidth / 2, y - 5);

        // 绘制标签
        ctx.fillStyle = '#b0c4de';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(labels[index], x + barWidth / 2, height - 10);
      });
    },
    drawLine(ctx, datasets, labels, colors, width, height) {
      const padding = 30; // 减少padding，确保底部网格线不被裁剪
      const chartWidth = width - padding * 2;
      const chartHeight = height - padding * 2;

      ctx.clearRect(0, 0, width, height);

      // 绘制网格
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }

      datasets.forEach((data, datasetIndex) => {
        const maxValue = Math.max(...data);

        // 绘制数据线
        ctx.strokeStyle = colors[datasetIndex];
        ctx.lineWidth = 3;
        ctx.beginPath();

        data.forEach((value, index) => {
          const x = padding + (chartWidth / (data.length - 1)) * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;

          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();

        // 绘制数据点
        ctx.fillStyle = colors[datasetIndex];
        data.forEach((value, index) => {
          const x = padding + (chartWidth / (data.length - 1)) * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;

          ctx.beginPath();
          ctx.arc(x, y, 5, 0, Math.PI * 2);
          ctx.fill();
        });
      });
    },

    drawHorizontalBar(ctx, data, labels, color, width, height) {
      const padding = 40;
      const chartWidth = width - padding * 2;
      const chartHeight = height - padding * 2;
      const barHeight = chartHeight / data.length * 0.6;
      const gap = chartHeight / data.length * 0.4;

      ctx.clearRect(0, 0, width, height);

      // 绘制网格
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.1)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const x = padding + (chartWidth / 5) * i;
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, height - padding);
        ctx.stroke();
      }

      // 绘制水平柱状图
      data.forEach((value, index) => {
        const y = padding + index * (barHeight + gap);
        const barWidth = (value / Math.max(...data)) * chartWidth;

        ctx.fillStyle = color;
        ctx.fillRect(padding, y, barWidth, barHeight);

        // 绘制数值
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(value, padding + barWidth + 5, y + barHeight / 2 + 4);

        // 绘制标签
        ctx.fillStyle = '#b0c4de';
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(labels[index], padding - 5, y + barHeight / 2 + 4);
      });
    },
    handleCommand(command) {
      if (command === 'logout') {
        this.logout();
      }
    },
    logout() {
      this.$confirm('确定要退出登录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'dark-message-box'
      }).then(() => {
        localStorage.removeItem('user');
        this.$router.push('/');
      }).catch(() => {});
    },

  }
};
</script>

<style scoped>
.energy-management-platform {
  width: 100vw;
  height: 100vh;
  background-image: url('../assets/img/shawushawu.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: #ffffff;
  font-family: 'Microsoft YaHei', 'Arial', sans-serif;
  overflow: hidden;
  position: relative;
}

/* 优化颜色方案，参考示例图片的专业工业监控平台风格 */
:root {
  --primary-color: #00d4ff;
  --secondary-color: #28a745;
  --accent-color: #007bff;
  --warning-color: #ffc107;
  --danger-color: #dc3545;
  --text-color: #ffffff;
  --text-secondary: #b0c4de;
  --bg-primary: rgba(26, 35, 50, 0.2);
  --bg-secondary: rgba(0, 0, 0, 0.1);
  --border-color: rgba(0, 212, 255, 0.2);
  --shadow-color: rgba(0, 212, 255, 0.3);
}

/* 背景装饰 - 简化背景效果，减少GPU负载 */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* 简化网格背景，减少绘制复杂度 */
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.02) 1px, transparent 1px);
  background-size: 100px 100px;
}

.blue-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  /* 简化发光效果，减少模糊计算 */
  background: radial-gradient(circle, rgba(0, 212, 255, 0.08) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(30px);
  z-index: 1;
}

/* 顶部标题栏 */
.top-header {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 12px 30px;
  background: rgba(26, 35, 50, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.top-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 212, 255, 0.2);
  border-color: rgba(0, 212, 255, 0.4);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 30px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-icon {
  font-size: 32px;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.8);
}

.logo h2 {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #00d4ff, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 主导航菜单 */
.main-nav {
  margin-left: 30px;
}

.nav-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 10px;
}

.nav-item {
  margin: 0;
  padding: 0;
}

.nav-link {
  display: block;
  padding: 10px 20px;
  color: #b0c4de;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;
}

.nav-link:hover {
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.1);
}

.nav-link.active {
  color: #ffffff;
  background: rgba(0, 123, 255, 0.2);
  border: 1px solid rgba(0, 123, 255, 0.4);
  box-shadow: 0 0 15px rgba(0, 123, 255, 0.3);
}

/* 设备列表弹窗 */
.device-list-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 999;
}

.modal-content {
  position: relative;
  background: rgba(26, 35, 50, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  width: 90%;
  max-width: 1200px;
  max-height: 80vh;
  overflow: hidden;
  z-index: 1001;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(13, 20, 30, 0.8);
}

.modal-header h2 {
  margin: 0;
  color: #00d4ff;
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.close-btn {
  font-size: 30px;
  color: #b0c4de;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
}

.close-btn:hover {
  color: #ffffff;
  background: rgba(220, 53, 69, 0.5);
  transform: rotate(90deg);
}

.modal-body {
  padding: 30px;
  max-height: calc(80vh - 100px);
  overflow-y: auto;
}

.device-table-container {
  overflow-x: auto;
}

.device-table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(13, 20, 30, 0.5);
  border-radius: 8px;
  overflow: hidden;
}

.device-table th,
.device-table td {
  padding: 15px;
  text-align: left;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  color: #ffffff;
  font-size: 14px;
}

.device-table th {
  background: rgba(0, 212, 255, 0.1);
  font-weight: 600;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.device-table tr:hover {
  background: rgba(0, 212, 255, 0.05);
}

.device-table tr:last-child td {
  border-bottom: none;
}

/* 状态徽章 */
.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.online {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.5);
}

.status-badge.warning {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.5);
}

.status-badge.offline {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.5);
}

/* 操作按钮 */
.action-btn {
  padding: 6px 16px;
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 6px;
  color: #00d4ff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(0, 212, 255, 0.4);
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  transform: translateY(-2px);
}

/* 设备详情弹窗样式 */
.device-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.device-detail-modal .modal-content {
  position: relative;
  background: rgba(26, 35, 50, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  z-index: 1001;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.device-detail-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(13, 20, 30, 0.8);
}

.device-detail-modal .modal-header h2 {
  margin: 0;
  color: #00d4ff;
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.device-detail-modal .close-btn {
  font-size: 30px;
  color: #b0c4de;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
}

.device-detail-modal .close-btn:hover {
  color: #ffffff;
  background: rgba(220, 53, 69, 0.5);
  transform: rotate(90deg);
}

/* 告警详情弹窗样式 */
.alarm-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alarm-detail-modal .modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 999;
}

.alarm-detail-modal .modal-content {
  position: relative;
  background: rgba(26, 35, 50, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  z-index: 1001;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.alarm-detail-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(13, 20, 30, 0.8);
}

.alarm-detail-modal .modal-header h2 {
  margin: 0;
  color: #00d4ff;
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.alarm-detail-modal .close-btn {
  font-size: 30px;
  color: #b0c4de;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
}

.alarm-detail-modal .close-btn:hover {
  color: #ffffff;
  background: rgba(220, 53, 69, 0.5);
  transform: rotate(90deg);
}

.alarm-detail-modal .modal-body {
  padding: 30px;
  max-height: calc(80vh - 100px);
  overflow: auto;
}

.alarm-detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

/* 告警表格容器 */
.alarm-table-container {
  width: 100%;
  overflow-x: auto;
  background: rgba(13, 20, 30, 0.5);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

/* 告警表格 */
.alarm-table {
  width: 100%;
  border-collapse: collapse;
  color: #ffffff;
}

/* 表格头部 */
.alarm-table th {
  background: rgba(0, 123, 255, 0.2);
  border-bottom: 1px solid rgba(0, 123, 255, 0.3);
  padding: 12px 15px;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

/* 表格单元格 */
.alarm-table td {
  border-bottom: 1px solid rgba(0, 123, 255, 0.1);
  padding: 12px 15px;
  font-size: 14px;
}

/* 表格行悬停效果 */
.alarm-table tbody tr:hover {
  background: rgba(0, 123, 255, 0.1);
  cursor: pointer;
}

/* 表格行最后一个单元格 */
.alarm-table td:last-child {
  text-align: center;
}

/* 状态徽章 */
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.warning {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.5);
}

.status-badge.success {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.5);
}

.status-badge.danger {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.5);
}

/* 设备详情内容容器 */
.device-detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

/* 设备基本信息 */
.device-basic-info {
  background: rgba(13, 20, 30, 0.9);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  padding: 25px;
  backdrop-filter: blur(5px);
}

/* 设备控制区域 */
.device-control-section {
  background: rgba(13, 20, 30, 0.9);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  padding: 25px;
  backdrop-filter: blur(5px);
}

/* 区域标题样式 */
.section-title {
  margin: 0 0 20px 0;
  color: #00d4ff;
  font-size: 20px;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  padding-bottom: 10px;
}

/* 控制子标题 */
.control-subtitle {
  margin: 0 0 15px 0;
  color: #b0c4de;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 信息网格布局 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

/* 信息项样式 */
.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* 信息标签 */
.info-label {
  color: #8892b0;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 信息值 */
.info-value {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
}

/* 高亮文本 */
.info-value.highlight {
  color: #00d4ff;
  font-size: 18px;
  text-shadow: 0 0 8px rgba(0, 212, 255, 0.5);
}

/* 控制区域 */
.control-section {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
}

.control-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

/* 控制按钮组 */
.control-buttons {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 0 20px;
  flex-wrap: wrap;
}

/* 控制按钮 */
.control-btn {
  padding: 12px 30px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 120px;
}

.control-btn.start-btn {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.8), rgba(32, 144, 58, 0.8));
  color: #ffffff;
  border: 1px solid rgba(40, 167, 69, 0.6);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.2);
}

.control-btn.start-btn:hover {
  background: linear-gradient(135deg, rgba(40, 167, 69, 1), rgba(32, 144, 58, 1));
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
  transform: translateY(-2px);
}

.control-btn.stop-btn {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.8), rgba(200, 35, 50, 0.8));
  color: #ffffff;
  border: 1px solid rgba(220, 53, 69, 0.6);
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);
}

.control-btn.stop-btn:hover {
  background: linear-gradient(135deg, rgba(220, 53, 69, 1), rgba(200, 35, 50, 1));
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
  transform: translateY(-2px);
}

/* 频率控制 */
.frequency-control {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* 频率输入组 */
.frequency-input-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* 频率输入容器 */
.frequency-input-container {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 10px 15px;
}

/* 频率输入框 */
.frequency-input {
  flex: 1;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s ease;
}

.frequency-input:focus {
  outline: none;
  border-color: #00d4ff;
  box-shadow: 0 0 15px rgba(0, 212, 255, 0.3);
  background: rgba(0, 0, 0, 0.5);
}

/* 频率单位 */
.frequency-unit {
  color: #00d4ff;
  font-weight: 700;
  font-size: 20px;
  text-shadow: 0 0 8px rgba(0, 212, 255, 0.5);
  min-width: 30px;
  text-align: center;
}

/* 频率按钮 */
.frequency-btn {
  padding: 12px 30px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.3), rgba(0, 180, 220, 0.3));
  border: 1px solid rgba(0, 212, 255, 0.6);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.2);
  white-space: nowrap;
}

/* 设备列表滚动条 */
.modal-body::-webkit-scrollbar,
.device-table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modal-body::-webkit-scrollbar-track,
.device-table-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb,
.device-table-container::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.5);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover,
.device-table-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.8);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 30px;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 20px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00ff00;
  animation: pulse 2s infinite;
}

.status-dot.online { background: #00ff00; }
.status-dot.warning { background: #ffc107; }
.status-dot.offline { background: #dc3545; }

.status-label {
  color: #b0c4de;
  font-size: 14px;
}

.status-value {
  color: #00d4ff;
  font-weight: 600;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 15px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.user-info:hover {
  background: rgba(0, 212, 255, 0.2);
  border-color: rgba(0, 212, 255, 0.5);
}

/* 下拉菜单样式 */
.dark-dropdown-menu {
  background-color: #1a2332 !important;
  border: 1px solid rgba(0, 212, 255, 0.3) !important;
  padding: 5px 0 !important;
}

.dark-dropdown-menu .el-dropdown-menu__item {
  color: #b0c4de !important;
}

.dark-dropdown-menu .el-dropdown-menu__item:hover {
  background-color: rgba(0, 212, 255, 0.1) !important;
  color: #00d4ff !important;
}

.dark-dropdown-menu .popper__arrow::after {
  border-bottom-color: #1a2332 !important;
}

.user-avatar {
  font-size: 18px;
}

/* 主要内容区域 */
.main-content {
  position: relative;
  z-index: 2;
  padding: 20px;
  height: calc(100vh - 70px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* 关键指标统计 */
.metrics-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.metric-card {
  background: rgba(26, 35, 50, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 123, 255, 0.4);
  border-color: rgba(0, 212, 255, 0.5);
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #00d4ff, #007bff);
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.metric-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #b0c4de;
  margin: 0;
}

.metric-icon {
  font-size: 24px;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 10px;
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.metric-value .unit {
  font-size: 16px;
  color: #00d4ff;
}

.metric-trend {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
}

.metric-trend.up {
  color: #28a745;
}

.metric-trend.down {
  color: #dc3545;
}

.trend-icon {
  font-size: 12px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #28a745);
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* 核心数据展示区 */
.core-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.left-charts, .right-data {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 380px;
}

.left-charts {
  align-self: flex-start;
  margin-top: -20px;
}

.right-data {
  align-self: flex-start;
  margin-top: -20px;
}

.left-charts .chart-card,
.right-data .chart-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(26, 35, 50, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  height: 410px;
  max-height: 410px;
  min-height: 410px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.left-charts .chart-card:hover,
.right-data .chart-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 123, 255, 0.4);
  border-color: rgba(0, 212, 255, 0.5);
}

.left-charts .chart-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.1);
  overflow: visible;
  padding-top: 30px;
}

.right-data .chart-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.1);
  overflow: hidden;
  position: relative;
}

/* 设备预警画布容器 */
.device-alarm-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alarm-chart-section {
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* 确保左侧图表容器占满高度 */
.left-charts .chart-card {
  height: 410px;
  max-height: 410px;
  min-height: 410px;
  box-sizing: border-box;
}

.right-data .chart-card {
  height: 410px;
  max-height: 410px;
  min-height: 410px;
  box-sizing: border-box;
}

.chart-card {
  background: rgba(26, 35, 50, 0.2);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 123, 255, 0.4);
  border-color: rgba(0, 212, 255, 0.5);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 5px;
  height: 32px;
  box-sizing: border-box;
  border-bottom: none;
}

.chart-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #00d4ff;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.5);
  background: linear-gradient(90deg, #00d4ff, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.chart-time {
  font-size: 12px;
  color: #b0c4de;
}

.chart-legend {
  display: flex;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #b0c4de;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.chart-container {
  position: relative;
  background: rgba(0, 30, 60, 0.15);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid rgba(0, 123, 255, 0.2);
  transition: all 0.3s ease;
  box-shadow: inset 0 0 20px rgba(0, 123, 255, 0.1);
}

.chart-container:hover {
  background: rgba(0, 30, 60, 0.2);
  border-color: rgba(0, 212, 255, 0.4);
  box-shadow: inset 0 0 30px rgba(0, 123, 255, 0.2);
}

/* 核心操作面板区域 */
.core-panel-area {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 500px;
  background: rgba(26, 35, 50, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  cursor: pointer;
}

/* Plexus神经网络网格容器 */
.plexus-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

/* Plexus神经网络网格画布 */
.plexus-canvas {
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(0, 212, 255, 0.05), transparent 70%);
}

/* 全息卡片容器 */
.hologram-cards {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}

.hologram-card {
  position: absolute;
  width: 150px;
  height: 120px;
  background: rgba(26, 35, 50, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.3), inset 0 0 20px rgba(0, 212, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: all;
  overflow: hidden;
  cursor: pointer;
}

/* 卡片悬浮效果 */
.hologram-card:hover {
  transform: translateY(-10px) scale(1.05);
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.5), inset 0 0 20px rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
}

/* 卡片位置 */
.hologram-card:nth-child(1) { top: 50px; left: 50px; }
.hologram-card:nth-child(2) { top: 50px; right: 50px; }
.hologram-card:nth-child(3) { bottom: 80px; left: 50px; }
.hologram-card:nth-child(4) { bottom: 80px; right: 50px; }

/* 卡片内容 */
.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

/* 卡片图标 */
.card-icon {
  font-size: 32px;
  margin-bottom: 10px;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.8);
}

/* 卡片标签 */
.card-label {
  font-size: 12px;
  color: #b0c4de;
  margin-bottom: 5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 卡片数值 */
.card-value {
  font-size: 24px;
  font-weight: 700;
  color: #00d4ff;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.8);
}

/* 卡片扫描线 */
.card-scan-line {
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
  transform: scaleX(0);
  transition: all 0.3s ease;
  z-index: 2;
}

/* 卡片扫描线激活状态 */
.card-scan-line.active {
  animation: scanLine 1.5s ease-in-out;
}

/* 扫描线动画 */
@keyframes scanLine {
  0% {
    top: -10%;
    transform: scaleX(1);
  }
  100% {
    top: 110%;
    transform: scaleX(1);
  }
}

/* 中心标题 */
.center-title {
  position: relative;
  text-align: center;
  z-index: 10;
}

.center-title h2 {
  font-size: 36px;
  font-weight: 700;
  color: #00d4ff;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.8);
  margin: 0 0 10px 0;
  background: linear-gradient(90deg, #00d4ff, #28a745);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 全宽图表 */
.chart-card.full-width {
  width: 100%;
}

/* 底部三段式布局 */
.bottom-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 20px 0;
}

.bottom-section .chart-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(26, 35, 50, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 16px;
  padding: 15px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  min-height: 380px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.bottom-section .chart-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 123, 255, 0.4);
  border-color: rgba(0, 212, 255, 0.5);
}

.bottom-section .chart-container {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 0;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.1);
  overflow: visible;
  padding-top: 10px;
}

/* 总耗电量趋势区域 */
.trend-section {
  background: rgba(26, 35, 50, 0.2);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  margin-bottom: 30px;
  min-height: 320px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.trend-section:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 123, 255, 0.4);
  border-color: rgba(0, 212, 255, 0.5);
}

.chart-container.full-height {
  height: 280px;
  width: 100%;
  padding: 15px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.1);
}

.chart-tabs {
  display: flex;
  gap: 10px;
}

.tab-item {
  padding: 8px 20px;
  background: rgba(0, 30, 60, 0.3);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 20px;
  font-size: 14px;
  color: #b0c4de;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 123, 255, 0.1);
}

.tab-item:hover, .tab-item.active {
  background: rgba(0, 123, 255, 0.2);
  color: #ffffff;
  border-color: #00d4ff;
  box-shadow: 0 4px 20px rgba(0, 123, 255, 0.4);
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.8);
}

/* 动画效果 */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 滚动条样式 */
.main-content::-webkit-scrollbar {
  width: 8px;
}

.main-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.main-content::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.5);
  border-radius: 4px;
}

.main-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.8);
}

/* 设备预警模块样式 */
.device-alarm-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 5px;
  box-sizing: border-box;
}

.alarm-chart-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(13, 20, 30, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(33, 150, 243, 0.3);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(33, 150, 243, 0.2);
  padding: 10px;
  box-sizing: border-box;
}

.alarm-data-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  height: 100%;
  justify-content: center;
}

.alarm-stat {
  background: rgba(13, 20, 30, 0.95);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  text-align: left;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(33, 150, 243, 0.2);
  backdrop-filter: blur(5px);
  width: 100%;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.alarm-stat:hover {
  box-shadow: 0 5px 18px rgba(33, 150, 243, 0.3), inset 0 1px 0 rgba(33, 150, 243, 0.4);
  transform: translateY(-2px);
}

.alarm-stat .stat-label {
  display: block;
  font-size: 13px;
  color: #b0c4de;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alarm-stat .stat-value {
  display: inline-block;
  font-size: 28px;
  font-weight: 700;
  color: #2196f3;
  text-shadow: 0 0 12px rgba(33, 150, 243, 0.8);
}

.alarm-stat .stat-unit {
  font-size: 16px;
  color: #ffffff;
  margin-left: 5px;
  font-weight: normal;
}

/* 确保左侧和右侧图表区域高度一致 */
.left-charts .chart-container,
.right-data .chart-container {
  height: 250px;
  width: 100%;
  padding: 15px;
  box-sizing: border-box;
}

/* 调整Canvas大小，让图表占满整个容器 */
.alarm-chart-section canvas {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}
</style>



// WEBPACK FOOTER //
// src/views/test.vue